import {
	type Offer,
	type OfferCollection,
	OfferEngineType,
	apiAppBusinessOfferSaveorder,
	apiAppBusinessOffercollectionSaveorder,
	apiAppBusinessOffercollectionUpsert,
	apiAppBusinessOffercollectionDelete,
	apiAppBusinessOfferUpsertprices,
	apiAppBusinessOfferUpsert,
	apiAppBusinessOfferDelete,
	apiAppBusinessOfferDuplicate,
	apiAppBusinessOfferSavereconfirmsettings
} from '#lib/types/generated/index.js';
import { toSaveOfferRequest } from '#lib/helper/offer.js';
import {
	optimisticArchive,
	optimisticDelete,
	optimisticReorder,
	optimisticSave
} from '#lib/stores/optimistic.js';
import { reloadSessions } from '#lib/stores/session.js';
import { get, writable } from 'svelte/store';

export const offerStore = writable<Offer[]>([]);
export const offerCollectionStore = writable<OfferCollection[]>([]);

export function saveOfferOrder(offers: Offer[]): Promise<void> {
	const sorted = [...offers].sort((a, b) => a.sortOrder - b.sortOrder);
	const offerIds = sorted.map((o) => o.id);
	return optimisticReorder(offerStore, sorted, () =>
		apiAppBusinessOfferSaveorder({ order: offerIds }).then(() => {})
	);
}

export function saveOfferCollectionOrder(collections: OfferCollection[]): Promise<void> {
	const sorted = [...collections].sort((a, b) => a.sortOrder - b.sortOrder);
	const offerCollectionIds = sorted.map((o) => o.id);
	return optimisticReorder(offerCollectionStore, sorted, () =>
		apiAppBusinessOffercollectionSaveorder({ order: offerCollectionIds }).then(() => {})
	);
}

export function saveOfferCollection(offerCollection: OfferCollection): Promise<OfferCollection> {
	return optimisticSave(offerCollectionStore, offerCollection, () =>
		apiAppBusinessOffercollectionUpsert(offerCollection.id, {
			name: offerCollection.name,
			offers: offerCollection.offers,
			color: offerCollection.color,
			description: offerCollection.description,
			logo: offerCollection.logo
		})
	);
}

export function deleteOfferCollection(offerCollection: OfferCollection): Promise<void> {
	return optimisticDelete(offerCollectionStore, offerCollection, () =>
		apiAppBusinessOffercollectionDelete(offerCollection.id)
	);
}

export function saveOfferGuestPrices(offer: Offer): Promise<Offer> {
	return apiAppBusinessOfferUpsertprices(offer.id, {
		prices: offer.guestPrices.map((gp) => ({
			...gp,
			archivedAt: gp.archivedAt ? gp.archivedAt.toISO() : null
		}))
	});
}

export function saveOffer(offer: Offer): Promise<Offer> {
	return optimisticSave(offerStore, offer, () =>
		apiAppBusinessOfferUpsert(offer.id, toSaveOfferRequest(offer)).then(() =>
			saveOfferGuestPrices(offer)
		)
	);
}

export function deleteOffer(offer: Offer): Promise<void> {
	return optimisticArchive(offerStore, offer, () => apiAppBusinessOfferDelete(offer.id));
}

export function duplicateOffer(offer: Offer): Promise<Offer> {
	return apiAppBusinessOfferDuplicate(offer.id).then(async (newOffer) => {
		offerStore.set([...get(offerStore), newOffer]);
		if (newOffer.engine === OfferEngineType.Event) {
			await reloadSessions();
		}
		return newOffer;
	});
}

export function saveOffersReconfirmSettings(
	offers: Array<{ id: string; reconfirmTimeBefore: number | null }>
): Promise<Offer[]> {
	return apiAppBusinessOfferSavereconfirmsettings({ offers }).then((res) => {
		offerStore.set(res.items);
		return res.items;
	});
}
